import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { transactionService } from '../services/transactionService';
import { 
  Inbox, 
  Phone, 
  Mail, 
  Search, 
  Filter,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  MessageSquare,
  Calendar,
  ExternalLink,
  Plus,
  MoreVertical,
  MessageCircle
} from 'lucide-react';

interface CustomerInquiry {
  id: string;
  shop_id: number;
  product_id?: number;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  customer_message?: string;
  product_name?: string;
  interaction_type: string;
  created_at?: string;
  campaign?: string;
  method?: string;
  status: 'new' | 'contacted' | 'resolved';
  // Channel Attribution Fields
  channel?: string;
  source?: string;
  post_id?: string;
  session_id?: string;
  attribution_data?: any;
}

interface CustomerInquiriesProps {
  onBack?: () => void;
}

export function CustomerInquiries({ onBack }: CustomerInquiriesProps) {
  const { user, token } = useAuth();
  const [inquiries, setInquiries] = useState<CustomerInquiry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'contacted' | 'resolved'>('all');
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | 'all'>('week');
  const [refreshing, setRefreshing] = useState(false);

  // Reply Modal State
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  const [currentInquiryForReply, setCurrentInquiryForReply] = useState<CustomerInquiry | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  // Fetch customer inquiries via N8N webhook
  const fetchInquiries = async () => {
    if (!user) {
      setError('User not authenticated');
      return;
    }
    
    if (user.role !== 'shop_admin' && user.role !== 'mall_admin' && user.role !== 'super_admin') {
      setError('User does not have permission to view customer inquiries');
      return;
    }
    
    if (!user.shop_id) {
      setError('User does not have a valid shop_id assigned');
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      const shopId = user.shop_id; // Get the shop_id directly from user object
      console.log('🎯 User object:', {
        user_id: user.id,
        username: user.username,
        role: user.role,
        shop_id: user.shop_id,
        mall_id: user.mall_id,
        shop_access: user.shop_access,
        mall_access: user.mall_access
      });
      console.log('🎯 Fetching inquiries for shop via N8N webhook:', shopId);
      console.log('🎯 Expected vs Actual: Shop', shopId, 'should return multiple records, checking why only 1 is shown');

      // Validate shop_id
      if (!shopId) {
        throw new Error(`Invalid shop_id: ${shopId}. User shop_id is ${user.shop_id}`);
      }

      // Get user token for authentication
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Build date filters
      const dateFilters: any = {};
      const now = new Date();
      
      switch (dateFilter) {
        case 'today':
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          dateFilters.dateFrom = today.toISOString();
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          dateFilters.dateFrom = weekAgo.toISOString();
          break;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          dateFilters.dateFrom = monthAgo.toISOString();
          break;
        default:
          // No date filter
          break;
      }

      // Call N8N webhook to get transactions
      const response = await transactionService.getTransactions(
        shopId, 
        token, 
        {
          ...dateFilters,
          limit: 100
        }
      );

      console.log('📡 N8N Response:', response);
      console.log('🔍 Response structure analysis:', {
        responseType: typeof response,
        hasSuccess: 'success' in response,
        hasData: 'data' in response,
        responseData: response.data,
        responseDataType: typeof response.data,
        responseDataIsArray: Array.isArray(response.data),
        responseDataDataIsArray: Array.isArray(response.data?.data)
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch inquiries');
      }

      // Transform the response data to match our interface
      // N8N returns the data wrapped in a json object: {json: {customer_name: "...", ...}}
      let inquiriesData: any[] = [];
      
      console.log('🔍 Analyzing response.data structure:', response.data);
      console.log('🔍 Full response structure:', JSON.stringify(response, null, 2));
      
      // Handle N8N json wrapper format: {json: {customer_name: "...", ...}}
      if (response.data && response.data.json) {
        if (Array.isArray(response.data.json)) {
          inquiriesData = response.data.json;
          console.log('✅ Found array in response.data.json');
        } else if (response.data.json && typeof response.data.json === 'object' && response.data.json.id) {
          inquiriesData = [response.data.json];
          console.log('✅ Found single object in response.data.json, wrapped in array');
        } else {
          inquiriesData = [response.data.json];
          console.log('✅ Found object in response.data.json, wrapped in array');
        }
      }
      // Fallback: Handle direct array format (response.data = [...])
      else if (Array.isArray(response.data)) {
        inquiriesData = response.data;
        console.log('✅ Found direct array in response.data');
      }
      // Fallback: Handle nested array (response.data.data = [...])
      else if (response.data && Array.isArray(response.data.data)) {
        inquiriesData = response.data.data;
        console.log('✅ Found nested array in response.data.data');
      }
      // Fallback: Handle single object (response.data = {...})
      else if (response.data && typeof response.data === 'object' && response.data.id) {
        inquiriesData = [response.data];
        console.log('✅ Found single object in response.data, wrapped in array');
      }
      // Response might be the array directly (response = [...])
      else if (Array.isArray(response)) {
        inquiriesData = response;
        console.log('✅ Found array directly in response');
      }
      else {
        console.log('⚠️ Unable to extract array data. Full response:', JSON.stringify(response, null, 2));
        console.log('⚠️ Response.data type:', typeof response.data);
        console.log('⚠️ Response.data keys:', Object.keys(response.data || {}));
        inquiriesData = [];
      }
      
      console.log('🎯 Extracted inquiries data:', inquiriesData.length, 'items');
      console.log('🎯 Sample inquiry data:', inquiriesData[0]);
      console.log('🎯 All inquiry IDs:', inquiriesData.map(item => item.id));
      console.log('🎯 First inquiry full data:', JSON.stringify(inquiriesData[0], null, 2));
      console.log('🎯 Raw N8N data structure check:', {
        totalRecords: inquiriesData.length,
        sampleRecords: inquiriesData.slice(0, 3).map(item => ({
          id: item.id,
          shop_id: item.shop_id,
          timestamp: item.timestamp,
          method: item.method,
          // Show available fields that might contain customer data
          session_id: item.session_id,
          product_id: item.product_id,
          campaign: item.campaign,
          // Check for potential message fields
          message: item.message,
          content: item.content,
          text: item.text,
          body: item.body,
          // Show what fields are missing
          hasCustomerName: !!item.customer_name,
          hasCustomerPhone: !!item.customer_phone,
          hasCustomerEmail: !!item.customer_email,
          hasCustomerMessage: !!item.customer_message,
          hasCreatedAt: !!item.created_at,
          // Show all available keys
          availableKeys: Object.keys(item)
        }))
      });
      
      // Handle empty data gracefully
      if (inquiriesData.length === 0) {
        console.log('⚠️ No inquiries data found, setting empty array');
        setInquiries([]);
        return;
      }
      
      // Warn if we expect more records but got fewer
      if (inquiriesData.length === 1) {
        console.log('⚠️ WARNING: Only 1 record found but expected multiple records for shop', shopId);
        console.log('⚠️ This might indicate:');
        console.log('  1. N8N query is filtering to only latest record');
        console.log('  2. N8N workflow is returning only first record');
        console.log('  3. Database query has LIMIT 1 or similar restriction');
      }
      
      console.log('🚀 Starting to process', inquiriesData.length, 'inquiries...');
      console.log('🔍 Raw N8N data sample (first 2 records):', inquiriesData.slice(0, 2));
      console.log('🔍 Checking for product name fields in raw data:', inquiriesData.slice(0, 2).map(item => ({
        id: item.id,
        hasProductName: !!item.product_name,
        hasProduct: !!item.product,
        hasJson: !!item.json,
        jsonProductName: item.json?.product_name,
        jsonProduct: item.json?.product,
        rawProductName: item.product_name,
        rawProduct: item.product
      })));
      
      const inquiriesWithStatus = inquiriesData.map((inquiry: any, index: number) => {
        console.log(`🎯 Processing inquiry ${index + 1}:`, inquiry.id, inquiry.interaction_type);
        console.log(`🔍 Full inquiry data for debugging:`, JSON.stringify(inquiry, null, 2));
        
        // The actual customer data is nested in inquiry.json
        const actualData = inquiry.json || inquiry;
        
        console.log(`🔍 Data structure analysis for inquiry ${index + 1}:`, {
          inquiry: inquiry,
          actualData: actualData,
          hasJson: !!inquiry.json,
          directProductName: inquiry.product_name,
          nestedProductName: inquiry.json?.product_name,
          actualDataProductName: actualData.product_name
        });
        
        // Generate a unique customer identifier from available data
        const customerId = actualData.id?.toString() || `record-${index + 1}`;
        const sessionId = actualData.session_id || 'unknown';
        
        // Use real customer data from the database
        const customerName = actualData.customer_name || 
                            (sessionId !== 'unknown' ? `Customer ${sessionId}` : `Customer ${customerId}`);
        
        // Extract message content - use actual customer message
        const messageContent = actualData.customer_message || 
                              actualData.message || actualData.content || actualData.text || actualData.body ||
                              `WhatsApp inquiry about product ${actualData.product_id || 'N/A'}`;
        
        // Extract product name with comprehensive fallback logic
        // Check multiple possible locations where product name might be stored
        const extractedProductName = 
          // Try actualData first (most common)
          (actualData.product_name && actualData.product_name.trim() !== '' && actualData.product_name !== 'Product' ? actualData.product_name : null) ||
          // Try direct inquiry access 
          (inquiry.product_name && inquiry.product_name.trim() !== '' && inquiry.product_name !== 'Product' ? inquiry.product_name : null) ||
          // Try other possible field names
          (actualData.product && actualData.product.trim() !== '' && actualData.product !== 'Product' ? actualData.product : null) ||
          (actualData.product_name_from_db && actualData.product_name_from_db.trim() !== '' && actualData.product_name_from_db !== 'Product' ? actualData.product_name_from_db : null) ||
          (actualData.name && actualData.name.trim() !== '' && actualData.name !== 'Product' ? actualData.name : null) ||
          // Final fallback to product ID
          (actualData.product_id ? `Product ${actualData.product_id}` : 'Product');
        
        console.log(`🔍 Product name extraction for inquiry ${index + 1}:`, {
          inquiry: inquiry,
          actualData: actualData,
          extractedProductName: extractedProductName,
          availableFields: {
            actualData_product_name: actualData.product_name,
            inquiry_product_name: inquiry.product_name,
            product: actualData.product,
            product_name_from_db: actualData.product_name_from_db,
            name: actualData.name,
            product_id: actualData.product_id
          }
        });
        
        const finalInquiry = {
          ...actualData, // Spread all actual data
          status: getStatusFromInteraction(actualData),
          id: customerId,
          // Map N8N timestamp to created_at
          created_at: actualData.timestamp || actualData.created_at || actualData.date,
          // Use real customer data from the database
          customer_name: actualData.customer_name || customerName,
          customer_phone: actualData.customer_phone || '',
          customer_email: actualData.customer_email || '',
          customer_message: messageContent,
          product_name: extractedProductName,
          // Channel Attribution Fields
          channel: actualData.channel || actualData.source || 'direct',
          source: actualData.source || 'direct_visit',
          post_id: actualData.post_id,
          session_id: actualData.session_id,
          attribution_data: actualData.attribution_data || {}
        };

        console.log(`✅ Final inquiry object for ${index + 1}:`, {
          id: finalInquiry.id,
          product_name: finalInquiry.product_name,
          customer_name: finalInquiry.customer_name
        });

        return finalInquiry;
      });
      
      console.log('✅ Successfully processed inquiries:', inquiriesWithStatus.length);
      console.log('✅ Processed inquiry IDs:', inquiriesWithStatus.map(item => item.id));
      console.log('✅ First processed inquiry sample:', JSON.stringify(inquiriesWithStatus[0], null, 2));
      console.log('✅ Setting state with inquiries array...');
      
      setInquiries(inquiriesWithStatus);
      console.log('✅ State update completed. Check if inquiries array is updated.');

    } catch (err) {
      console.error('🎯 Error fetching inquiries via N8N webhook:', err);
      setError('Failed to load customer inquiries. Please try again.');
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchInquiries();
  };

  const getStatusFromInteraction = (inquiry: any) => {
    // Map interaction types to status
    switch (inquiry.interaction_type) {
      case 'inquiry_submission': return 'contacted';
      case 'inquiry_intent': return 'new';
      default: return 'new';
    }
  };

  // Load inquiries when component mounts or filters change
  useEffect(() => {
    fetchInquiries();
  }, [user, dateFilter]);

  // Filter inquiries based on search term and status
  const filteredInquiries = inquiries.filter(inquiry => {
    const matchesSearch = searchTerm === '' || 
      (inquiry.customer_name && inquiry.customer_name.trim() && inquiry.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (inquiry.customer_message && inquiry.customer_message.trim() && inquiry.customer_message.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (inquiry.product_name && inquiry.product_name.trim() && inquiry.product_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || inquiry.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Calculate statistics
  const stats = {
    total: inquiries.length,
    new: inquiries.filter(i => i.status === 'new').length,
    contacted: inquiries.filter(i => i.status === 'contacted').length,
    resolved: inquiries.filter(i => i.status === 'resolved').length,
    today: inquiries.filter(i => {
      if (!i.created_at) return false;
      const inquiryDate = new Date(i.created_at);
      const today = new Date();
      return inquiryDate.toDateString() === today.toDateString();
    }).length
  };

  const handleCallCustomer = (phone: string | undefined) => {
    if (!phone) {
      console.log('⚠️ No phone number available');
      return;
    }
    window.open(`tel:${phone}`, '_self');
  };

  const handleReplyOnWhatsApp = (inquiry: CustomerInquiry) => {
    if (!user) {
      console.log('⚠️ User not authenticated');
      alert('Please log in to reply via WhatsApp.');
      return;
    }

    const phone = inquiry.customer_phone?.replace(/[^0-9]/g, '');
    
    if (!phone) {
      console.log('⚠️ No phone number available for WhatsApp');
      alert('No phone number available for this customer.');
      return;
    }

    // Format phone number for WhatsApp (remove any non-numeric characters)
    const cleanPhone = phone.replace(/^0/, '254'); // Convert 07xx to 2547xx format
    
    // Create pre-filled message template
    const shopName = user.shop_name || 'your shop';
    const defaultMessage = encodeURIComponent(
      `Hello ${inquiry.customer_name || 'Customer'},\n\n` +
      `Thank you for your inquiry about ${inquiry.product_name || 'our products'}.\n\n` +
      `From ${shopName}\n\n` +
      `---\n\n` +
      `Reply from: ${user.username || 'Shop Admin'} (${shopName})`
    );

    // Open WhatsApp Web
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${defaultMessage}`;
    console.log('📱 Opening WhatsApp Web for:', cleanPhone);
    window.open(whatsappUrl, '_blank');
  };

  const handleEmailCustomer = (email: string | undefined) => {
    if (!email) {
      console.log('⚠️ No email address available');
      return;
    }
    window.open(`mailto:${email}`, '_self');
  };

  const handleUpdateInquiryStatus = async (inquiryId: string, newStatus: 'new' | 'contacted' | 'resolved') => {
    console.log('🔄 Starting status update for inquiry:', inquiryId, 'to status:', newStatus);
    try {
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Find the inquiry to get complete customer data
      const inquiry = inquiries.find(inq => inq.id === inquiryId);
      if (!inquiry) {
        throw new Error('Inquiry not found in local state');
      }

      console.log('🔄 Found inquiry data:', inquiry);
      
      // Update transaction status only (customer data is already stored)
      const response = await transactionService.updateTransaction(
        inquiryId,
        { 
          status: newStatus
        },
        token
      );

      console.log('🔄 Update response:', response);

      if (!response.success) {
        throw new Error(response.error || 'Failed to update inquiry status');
      }

      console.log('🔄 Updating local state...');
      // Update local state
      setInquiries(prev => 
        prev.map(inquiry => 
          inquiry.id === inquiryId 
            ? { ...inquiry, status: newStatus }
            : inquiry
        )
      );
      
      console.log('✅ Status update completed successfully');
    } catch (err) {
      console.error('❌ Error updating inquiry status:', err);
      setError('Failed to update inquiry status. Please try again.');
    }
  };

  const handleRespondToCustomer = (inquiry: CustomerInquiry) => {
    console.log('💬 Opening reply modal for inquiry:', inquiry.id);
    setCurrentInquiryForReply(inquiry);
    setReplyMessage('');
    setReplyError(null);
    setIsReplyModalOpen(true);
  };

  const handleSendReply = async () => {
    if (!currentInquiryForReply || !replyMessage.trim()) {
      setReplyError('Please enter a response message');
      return;
    }

    setIsSendingReply(true);
    setReplyError(null);

    try {
      if (!token) {
        throw new Error('Authentication token not found');
      }

      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log('📤 Sending reply to inquiry:', currentInquiryForReply.id);
      console.log('📝 Response message:', replyMessage);

      // Send response via N8N webhook (stored in DB, customer sees in app)
      const response = await transactionService.sendInquiryResponse(
        currentInquiryForReply.id,
        replyMessage.trim(),
        user.id,
        user.username || `Shop Owner ${user.shop_id}`,
        user.shop_id || 0,
        user.shop_name || 'Shop',
        token
      );

      console.log('📡 Send response result:', response);

      if (!response.success) {
        throw new Error(response.error || 'Failed to save response');
      }

      console.log('✅ Response saved successfully!');

      // Update inquiry status to "contacted" in local state
      setInquiries(prev =>
        prev.map(inquiry =>
          inquiry.id === currentInquiryForReply.id
            ? { ...inquiry, status: 'contacted' as const }
            : inquiry
        )
      );

      // Close modal and show success
      setIsReplyModalOpen(false);
      setCurrentInquiryForReply(null);
      setReplyMessage('');

      // Show success message
      alert(`✅ Response saved!\n\nYour reply will be visible to ${currentInquiryForReply.customer_name || 'the customer'} in the NHC Langata app when they next open it.\n\nMessage: "${replyMessage.trim()}"`);

    } catch (err) {
      console.error('❌ Error saving response:', err);
      setReplyError(err instanceof Error ? err.message : 'Failed to save response. Please try again.');
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleCloseReplyModal = () => {
    setIsReplyModalOpen(false);
    setCurrentInquiryForReply(null);
    setReplyMessage('');
    setReplyError(null);
  };

  const handleAddNotes = async (inquiry: any) => {
    try {
      const notes = prompt(`Add notes for ${inquiry.customer_name}:\n\nCurrent inquiry: "${inquiry.customer_message}"\n\nAdd your notes:`);
      if (notes && notes.trim()) {
        console.log('📝 Adding notes:', notes);
        alert(`Notes added for ${inquiry.customer_name}!\n\nNotes: "${notes}"\n\nNote: Notes would be saved to database in production.`);
      }
    } catch (error) {
      console.error('❌ Error adding notes:', error);
      alert('Failed to add notes. Please try again.');
    }
  };

  const handleAssignToTeam = async (inquiry: any) => {
    try {
      const assignee = prompt(`Assign ${inquiry.customer_name} to team member:\n\nAvailable team members:\n- John Doe (Sales)\n- Jane Smith (Support)\n- Mike Johnson (Manager)\n\nEnter assignee name:`);
      if (assignee && assignee.trim()) {
        console.log('👤 Assigning to:', assignee);
        alert(`${inquiry.customer_name} assigned to ${assignee}!\n\nNote: Assignment would be saved to database in production.`);
      }
    } catch (error) {
      console.error('❌ Error assigning to team:', error);
      alert('Failed to assign to team member. Please try again.');
    }
  };

  const handleExportData = (inquiry: any) => {
    try {
      const exportData = {
        customer_inquiry: {
          id: inquiry.id,
          customer_name: inquiry.customer_name,
          customer_phone: inquiry.customer_phone,
          customer_email: inquiry.customer_email,
          message: inquiry.customer_message,
          product_name: inquiry.product_name,
          interaction_type: inquiry.interaction_type,
          method: inquiry.method,
          campaign: inquiry.campaign,
          created_at: inquiry.created_at,
          shop_id: inquiry.shop_id,
          status: inquiry.status
        }
      };
      
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `customer-inquiry-${inquiry.id}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      console.log('📤 Exported inquiry data:', inquiry.id);
      alert(`Inquiry data exported successfully!\n\nFile: customer-inquiry-${inquiry.id}.json`);
    } catch (error) {
      console.error('❌ Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'contacted':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-yellow-100 text-yellow-800';
      case 'contacted':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getChannelDisplayName = (channel: string) => {
    const channelNames: { [key: string]: string } = {
      'whatsapp': 'WhatsApp',
      'qr_scan': 'QR Scan',
      'instagram': 'Instagram',
      'direct': 'Direct Visit'
    };
    return channelNames[channel] || channel;
  };

  const getChannelColor = (channel: string) => {
    const channelColors: { [key: string]: string } = {
      'whatsapp': 'bg-green-100 text-green-800',
      'qr_scan': 'bg-blue-100 text-blue-800',
      'instagram': 'bg-pink-100 text-pink-800',
      'direct': 'bg-gray-100 text-gray-800'
    };
    return channelColors[channel] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) {
        return 'Date not available';
      }
      
      // Handle different date formats
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.log('⚠️ Invalid date format:', dateString);
        return 'Invalid date format';
      }
      
      return date.toLocaleString();
    } catch (error) {
      console.log('⚠️ Date formatting error:', error, 'for date:', dateString);
      return 'Date formatting error';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-page-bg">
        {/* Header */}
        <header className="bg-white border-b border-border-subtle">
          <div className="container py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">
                  Customer Inquiries
                </h1>
                <p className="text-text-secondary mt-1">
                  Manage customer questions and follow-ups
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Loading State */}
        <main className="container py-8">
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3 text-text-secondary">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>Loading customer inquiries...</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page-bg">
      {/* Header */}
      <header className="bg-white border-b border-border-subtle">
        <div className="container py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-text-primary">
                Customer Inquiries
              </h1>
              <p className="text-text-secondary mt-1">
                Manage customer questions and follow-ups from your shop
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="btn-secondary flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="container py-8">
        {/* Error State */}
        {error && (
          <div className="bg-error/10 border border-error/20 rounded-md p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-error mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-error mb-1">Error Loading Inquiries</h3>
                <p className="text-error text-sm">{error}</p>
                <button
                  onClick={handleRefresh}
                  className="mt-3 text-error underline hover:no-underline text-sm"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-border-subtle p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-secondary">Total Inquiries</p>
                <p className="text-3xl font-bold text-text-primary">{stats.total}</p>
              </div>
              <Inbox className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-border-subtle p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-secondary">New</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.new}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-border-subtle p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-secondary">Contacted</p>
                <p className="text-3xl font-bold text-blue-600">{stats.contacted}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-border-subtle p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-secondary">Resolved</p>
                <p className="text-3xl font-bold text-green-600">{stats.resolved}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-border-subtle p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-secondary">Today</p>
                <p className="text-3xl font-bold text-purple-600">{stats.today}</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-border-subtle p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search customer name, message, or product..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            {/* Date Filter */}
            <div className="sm:w-48">
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>
        </div>

        {/* Inquiries List */}
        <div className="bg-white rounded-lg border border-border-subtle">
          {filteredInquiries.length === 0 ? (
            <div className="text-center py-12">
              <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No inquiries found
              </h3>
              <p className="text-gray-600">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria.' 
                  : 'Customer inquiries will appear here when visitors ask questions about your products.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredInquiries.map((inquiry) => (
                <div key={inquiry.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Customer Info */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-blue-100 p-2 rounded-full">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {(inquiry.customer_name && inquiry.customer_name.trim()) || `Customer ID: ${inquiry.id}`}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            {(inquiry.customer_phone && inquiry.customer_phone.trim()) && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {inquiry.customer_phone}
                              </span>
                            )}
                            {(inquiry.customer_email && inquiry.customer_email.trim()) && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {inquiry.customer_email}
                              </span>
                            )}
                            {(!inquiry.customer_phone || !inquiry.customer_phone.trim()) && (!inquiry.customer_email || !inquiry.customer_email.trim()) && (
                              <span className="text-gray-400">No contact info provided</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Inquiry Message */}
                      {(inquiry.customer_message && inquiry.customer_message.trim()) && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <div className="flex items-start gap-3">
                            <MessageSquare className="w-4 h-4 text-gray-500 mt-1 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-gray-900 mb-1">Customer Message:</p>
                              <p className="text-gray-700">{inquiry.customer_message}</p>
                              {(inquiry.product_name && inquiry.product_name.trim() && inquiry.product_name !== 'Product') && (
                                <p className="text-sm text-blue-600 mt-2">
                                  Product: {inquiry.product_name}
                                </p>
                              )}
                              {(!inquiry.product_name || !inquiry.product_name.trim() || inquiry.product_name === 'Product') && (
                                <p className="text-sm text-gray-600 mt-2">
                                  Product: {inquiry.product_name || `Product ID: ${inquiry.product_id?.toString() || 'Unknown'}`}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {(!inquiry.customer_message || !inquiry.customer_message.trim()) && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <div className="flex items-start gap-3">
                            <MessageSquare className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-gray-500 mb-1">No Message Provided</p>
                              <p className="text-gray-400 text-sm italic">Customer message not available in N8N data</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Metadata */}
                      <div className="flex items-center gap-6 text-xs text-gray-500">
                        {inquiry.created_at ? (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(inquiry.created_at)}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-400">
                            <Calendar className="w-3 h-3" />
                            Date not available
                          </span>
                        )}
                        <span>
                          Shop ID: {inquiry.shop_id}
                        </span>
                        {/* Channel Attribution Display */}
                        {inquiry.channel && inquiry.channel !== 'direct' && (
                          <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            📱 {getChannelDisplayName(inquiry.channel)}
                          </span>
                        )}
                        {inquiry.campaign && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                            🎯 {inquiry.campaign}
                          </span>
                        )}
                        {inquiry.post_id && (
                          <span className="text-xs text-gray-500">
                            Post: {inquiry.post_id}
                          </span>
                        )}
                        {inquiry.session_id && (
                          <span className="text-xs text-gray-400">
                            Session: {inquiry.session_id.slice(-8)}
                          </span>
                        )}
                        {inquiry.method && (
                          <span>
                            Via: {inquiry.method}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-start gap-3 ml-4">
                      {/* Status Badge */}
                      <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(inquiry.status)}`}>
                        {getStatusIcon(inquiry.status)}
                        {inquiry.status}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col gap-2">
                        {(inquiry.customer_phone && inquiry.customer_phone.trim()) && (
                          <button
                            onClick={() => {
                              console.log('📞 Call button clicked for phone:', inquiry.customer_phone);
                              handleCallCustomer(inquiry.customer_phone);
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors"
                          >
                            <Phone className="w-3 h-3" />
                            Call
                          </button>
                        )}
                        
                        {(inquiry.customer_email && inquiry.customer_email.trim()) && (
                          <button
                            onClick={() => {
                              console.log('📧 Email button clicked for email:', inquiry.customer_email);
                              handleEmailCustomer(inquiry.customer_email);
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors"
                          >
                            <Mail className="w-3 h-3" />
                            Email
                          </button>
                        )}
                        
                        {(!inquiry.customer_phone || !inquiry.customer_phone.trim()) && (!inquiry.customer_email || !inquiry.customer_email.trim()) && (
                          <button
                            onClick={() => {
                              console.log('⚠️ No contact info available for inquiry:', inquiry.id);
                              alert('No contact information available for this customer.');
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 bg-gray-400 text-white text-xs font-medium rounded-md cursor-not-allowed"
                            disabled
                          >
                            <Phone className="w-3 h-3" />
                            No Contact
                          </button>
                        )}

                        <div className="flex flex-col gap-1">
                          {/* Direct WhatsApp Reply Button */}
                          {inquiry.customer_phone && inquiry.customer_phone.trim() && (
                            <button 
                              onClick={() => handleReplyOnWhatsApp(inquiry)}
                              className="flex items-center gap-2 px-2 py-1 bg-[#25D366] hover:bg-[#20BD5A] text-white text-xs font-medium rounded-md transition-colors"
                              title="Reply directly on WhatsApp"
                            >
                              <MessageCircle className="w-3 h-3" />
                              WhatsApp
                            </button>
                          )}
                          
                          {/* In-App Quick Reply Button */}
                          {inquiry.method === 'whatsapp' && (
                            <button 
                              onClick={() => handleRespondToCustomer(inquiry)}
                              className="flex items-center gap-2 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors"
                            >
                              📱 Quick Reply
                            </button>
                          )}
                          
                          {inquiry.status === 'new' && (
                            <button 
                              onClick={() => {
                                console.log('🔄 Mark Contacted clicked for inquiry:', inquiry.id);
                                handleUpdateInquiryStatus(inquiry.id, 'contacted');
                              }}
                              className="flex items-center gap-2 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors"
                            >
                              Mark Contacted
                            </button>
                          )}
                          {inquiry.status === 'contacted' && (
                            <button 
                              onClick={() => {
                                console.log('🔄 Mark Resolved clicked for inquiry:', inquiry.id);
                                handleUpdateInquiryStatus(inquiry.id, 'resolved');
                              }}
                              className="flex items-center gap-2 px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors"
                            >
                              Mark Resolved
                            </button>
                          )}
                          <div className="relative group">
                            <button 
                              className="flex items-center gap-2 px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-md transition-colors"
                            >
                              <MoreVertical className="w-3 h-3" />
                              More
                            </button>
                            
                            {/* Dropdown Menu */}
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                              <button 
                                onClick={() => handleRespondToCustomer(inquiry)}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-md"
                              >
                                💬 Respond to Customer
                              </button>
                              <button 
                                onClick={() => handleAddNotes(inquiry)}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                📝 Add Notes
                              </button>
                              <button 
                                onClick={() => handleAssignToTeam(inquiry)}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                👤 Assign to Team
                              </button>
                              <button 
                                onClick={() => handleExportData(inquiry)}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-md"
                              >
                                📤 Export Data
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Reply Modal */}
      {isReplyModalOpen && currentInquiryForReply && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-blue-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-white" />
                  <h2 className="text-lg font-semibold text-white">
                    Reply to Customer
                  </h2>
                </div>
                <button
                  onClick={handleCloseReplyModal}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {currentInquiryForReply.customer_name || `Customer ID: ${currentInquiryForReply.id}`}
                    </h3>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      {currentInquiryForReply.customer_phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {currentInquiryForReply.customer_phone}
                        </span>
                      )}
                      {currentInquiryForReply.customer_email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {currentInquiryForReply.customer_email}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Original Message */}
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-1">Original Message:</p>
                  <div className="bg-white rounded border border-gray-200 p-3">
                    <p className="text-gray-700">
                      {currentInquiryForReply.customer_message || 'No message provided'}
                    </p>
                    {currentInquiryForReply.product_name && currentInquiryForReply.product_name !== 'Product' && (
                      <p className="text-sm text-blue-600 mt-2">
                        Product: {currentInquiryForReply.product_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Response Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Response <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your response to the customer... This will be sent via WhatsApp."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[150px] resize-y"
                  disabled={isSendingReply}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {replyMessage.length}/500 characters
                </p>
              </div>

              {/* Error Message */}
              {replyError && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-red-800">Error Sending Response</h4>
                      <p className="text-red-700 text-sm mt-1">{replyError}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="text-blue-600 font-medium">✓</span> Response saved to app
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCloseReplyModal}
                  disabled={isSendingReply}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendReply}
                  disabled={isSendingReply || !replyMessage.trim()}
                  className={`flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md transition-colors ${
                    isSendingReply || !replyMessage.trim()
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-blue-700'
                  }`}
                >
                  {isSendingReply ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="w-4 h-4" />
                      Save Response
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
